import { Check } from "lucide-react";
import { classNames } from "../../helpers/classNames";

/**
 * The Printable PDF offer on Review order (docs/design/HANDOFF.md, "Printable
 * PDF offer, redesigned"; Order, OrderCart, Tab6Order, Tab7Cart, Desk6Order,
 * Desk7Cart). Unticked by default: every charge comes from a tap.
 *
 * `PdfOfferCard` is "Start coloring today": for one book its button adds that
 * book's PDF; for several it adds or removes the PDF for every book, and the
 * per-book `PdfRow`s and the button always agree. Prices come in as numbers
 * ($5 with the book, $9.95 on its own).
 */

const price = (n) => (Number.isInteger(n) ? `$${n}` : `$${n.toFixed(2)}`);

/** Stacked pages marked "PDF". */
const PdfArt = () => (
  <div aria-hidden="true" className="relative h-[72px] w-14 flex-shrink-0">
    <div className="absolute left-2.5 top-0 h-[58px] w-11 rotate-6 rounded border border-[#D9CFC4] bg-white" />
    <div className="absolute left-0 top-2 flex h-[60px] w-[46px] items-center justify-center rounded border border-[#CFC4B8] bg-white text-md-pink-dark shadow-[0_2px_6px_rgba(27,13,18,0.12)]">
      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="9" r="4" />
        <path d="M5 21c1.5-4 4-6 7-6s5.5 2 7 6" />
      </svg>
    </div>
    <span className="absolute -right-0.5 bottom-0 rounded bg-md-pink-dark px-[5px] py-px text-[10px] font-bold text-white">PDF</span>
  </div>
);

/** The square tick box drawn inside the buttons. */
const Box = ({ on, size = 24 }) => (
  <span
    aria-hidden="true"
    style={{ width: size, height: size }}
    className={classNames(
      "flex flex-shrink-0 items-center justify-center rounded-md border-2 text-white",
      on ? "border-md-pink-dark bg-md-pink-dark" : "border-[#CFC4B8] bg-white"
    )}
  >
    {on ? <Check className="h-4 w-4" strokeWidth={3} /> : null}
  </span>
);

/**
 * "Start coloring today".
 * `count`: books in the order (1 = one book). `added`: how many have the PDF.
 * `onToggle()`: one book, flip it; several, add the rest or (all added) remove all.
 * `lead`: replaces "Get your book as a printable PDF" (one printed book beside a PDF-only one).
 * `inBook`: drawn inside the book's card (one book, tablet and desktop): beige until added.
 * `column`: the desktop order-summary column (several books, `Desk7Cart`): stacked like a phone, with
 * shorter button labels ("Add all 3 PDFs · $15") and "Or choose for each book on the left."
 */
export const PdfOfferCard = ({ count, added, onToggle, addOnPrice, fullPrice, disabled = false, inBook = false, column = false, lead, idSuffix = "", className }) => {
  const one = count === 1;
  const allOn = count > 0 && added === count;
  const left = count - added;
  const who = count === 2 ? "both books" : `all ${count} books`;
  const label = column && !one
    ? allOn
      ? count === 2 ? "Both PDFs added" : `All ${count} PDFs added`
      : added === 0
        ? `${count === 2 ? "Add both PDFs" : `Add all ${count} PDFs`} · ${price(addOnPrice * count)}`
        : left === 1
          ? `Add the other PDF · ${price(addOnPrice)}`
          : `Add the other ${left} PDFs · ${price(addOnPrice * left)}`
    : one
    ? allOn
      ? `PDF added for ${price(addOnPrice)}`
      : `Add the PDF for ${price(addOnPrice)}`
    : allOn
      ? `PDFs added for ${who}`
      : added === 0
        ? `Add PDFs for ${who} · ${price(addOnPrice * count)}`
        : left === 1
          ? `Add a PDF for the other book · ${price(addOnPrice)}`
          : `Add PDFs for the other ${left} books · ${price(addOnPrice * left)}`;
  const headingId = `pdf-offer${idSuffix}`;
  const anyOn = added > 0;

  return (
    <section
      aria-labelledby={headingId}
      className={classNames(
        "flex-col gap-3",
        column ? "rounded-[20px] p-[22px]" : inBook ? "rounded-[14px] p-4 md:gap-3.5 md:px-5 md:py-[18px]" : "rounded-[14px] p-4 md:gap-3.5 md:rounded-2xl md:px-6 md:py-5",
        (inBook ? allOn : anyOn) ? "border-2 border-md-pink-dark bg-white" : inBook ? "border border-[#E6DDD3] bg-[#FBF8F3]" : "border border-md-pink-light-1 bg-white",
        className || "flex"
      )}
    >
      <div className={classNames("flex items-start gap-3.5", !column && "md:gap-4")}>
        <PdfArt />
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <h2 id={headingId} className={classNames("m-0 font-sans text-[17px] font-bold leading-[1.3] text-md-dark", !column && "md:text-lg")}>
            Start coloring today
          </h2>
          <p className="m-0 text-[15px] leading-[1.45] text-[#4A4446]">
            {lead || `Get ${one ? "your book" : "each book"} as a printable PDF`}, emailed right away. Print any page again so everyone gets a turn, or print the entire book anytime you want.
          </p>
        </div>
      </div>
      <div className={classNames("flex flex-col gap-3", !column && "md:flex-row md:flex-wrap md:items-center md:justify-between md:gap-4 md:pl-[72px]")}>
        <div className="flex flex-col gap-1">
          <div className="flex flex-wrap items-baseline gap-2 text-[15px]">
            <span className="text-[17px] font-bold text-md-pink-dark">
              {price(addOnPrice)} with {one ? "your book" : "each book"}
            </span>
            <span className="text-md-muted">
              <span className="line-through">{price(fullPrice)}</span> on its own
            </span>
          </div>
          {!one && !column ? <span className="hidden text-[13px] text-md-muted md:block">Or choose for each book above.</span> : null}
        </div>
        <button
          type="button"
          role={one ? "checkbox" : undefined}
          aria-checked={one ? allOn : undefined}
          aria-pressed={one ? undefined : allOn}
          onClick={onToggle}
          disabled={disabled}
          className={classNames(
            "flex min-h-[52px] items-center justify-center gap-2.5 rounded-xl border-2 px-3.5 py-2 text-base font-semibold text-md-dark disabled:opacity-60",
            !column && "md:min-w-[240px]",
            allOn ? "border-md-pink-dark bg-[#F9EFF1]" : "border-[#D9CFC4] bg-white"
          )}
        >
          <Box on={allOn} />
          <span>{label}</span>
        </button>
        {!one ? (
          <span className={classNames("text-center text-[13px] text-md-muted", !column && "md:hidden")}>
            {column ? "Or choose for each book on the left." : "Or choose for each book above."}
          </span>
        ) : null}
      </div>
    </section>
  );
};

/** A book's compact "Printable PDF · $5" tick row (several books). */
export const PdfRow = ({ on, onToggle, title, addOnPrice, disabled = false }) => (
  <button
    type="button"
    role="checkbox"
    aria-checked={on}
    aria-label={`Printable PDF of ${title}, ${price(addOnPrice)}`}
    onClick={onToggle}
    disabled={disabled}
    className={classNames(
      "flex min-h-12 items-center gap-3 rounded-[10px] border px-3 py-1.5 text-left text-md-dark disabled:opacity-60 md:min-h-[52px] md:gap-3.5 md:px-4 md:py-2 lg:min-h-12",
      on ? "border-md-pink-dark bg-[#F9EFF1]" : "border-[#E6DDD3] bg-[#FBF8F3]"
    )}
  >
    <Box on={on} size={22} />
    <span className="flex-1 text-[15px] font-semibold md:text-base lg:text-[15px]">Printable PDF</span>
    <span className="text-[15px] font-bold text-md-pink-dark">{price(addOnPrice)}</span>
  </button>
);
